import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BackButton from '../components/BackButton';

export default function InvoiceEditPage() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customerId: '',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    notes: '',
    discount: 0,
  });

  const [items, setItems] = useState([
    { Description: '', HSN: '', Size: '', Quantity: '', Rate: '', GSTPercent: '0', CGST: '0', SGST: '0', Amount: '' }
  ]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // --- Product search modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productRowIdx, setProductRowIdx] = useState(null);

  useEffect(() => {
    fetchInvoice();
    fetchCustomers();
    // eslint-disable-next-line
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const res = await api.get(`/invoices/${invoiceId}`);
      const { invoice, items } = res.data;

      setForm({
        customerId: invoice.CustomerID,
        invoiceNumber: invoice.InvoiceNo,
        invoiceDate: invoice.InvoiceDate?.split('T')[0] || '',
        dueDate: invoice.DueDate ? invoice.DueDate.split('T')[0] : '',
        notes: invoice.Notes || '',
        discount: invoice.Discount || 0,
      });

      setSelectedCustomer({ value: invoice.CustomerID, label: invoice.CustomerName });

      const updatedItems = items.map(item => calculateItemAmount(item));
      setItems(updatedItems.length ? updatedItems : [{
        Description: '', HSN: '', Size: '', Quantity: '', Rate: '', GSTPercent: '0', CGST: '0', SGST: '0', Amount: ''
      }]);
    } catch (err) {
      toast.error('Failed to load invoice');
      console.error(err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/customers');
      setCustomers(res.data);
    } catch (err) {
      toast.error('Failed to load customers');
      console.error(err);
    }
  };

  const calculateItemAmount = (item) => {
    const qty = parseFloat(item.Quantity) || 0;
    const rate = parseFloat(item.Rate) || 0;
    const gstPercent = item.GSTPercent === '' || item.GSTPercent === undefined ? 0 : parseFloat(item.GSTPercent);
    const cgst = (gstPercent / 2).toFixed(2);
    const sgst = (gstPercent / 2).toFixed(2);
    const baseAmount = qty * rate;
    const gstAmount = baseAmount * gstPercent / 100;
    const total = baseAmount + gstAmount;
    return { ...item, GSTPercent: gstPercent, CGST: cgst, SGST: sgst, Amount: total.toFixed(2) };
  };

  // Always recalculate Amount when item fields change
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems.map((item, idx) => calculateItemAmount(item)));
  };

  const addItem = () => {
    setItems(prev =>
      [
        ...prev,
        { Description: '', HSN: '', Size: '', Quantity: '', Rate: '', GSTPercent: '0', CGST: '0', SGST: '0', Amount: '' }
      ]
    );
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Product modal open
  const handleOpenProductModal = idx => {
    setProductRowIdx(idx);
    setShowProductModal(true);
    setProductSearch('');
    setProductResults([]);
  };

  const fetchProductSearch = async (q) => {
    if (!q) {
      setProductResults([]);
      return;
    }
    try {
      const res = await api.get('/products', { params: { search: q } });
      setProductResults(res.data);
    } catch {
      setProductResults([]);
    }
  };

  // Totals
  const subTotal = items.reduce((sum, i) => sum + +(i.Amount || 0), 0);
  const discount = parseFloat(form.discount || 0);
  const grandTotal = (subTotal - discount > 0 ? subTotal - discount : 0).toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      customerId: selectedCustomer ? selectedCustomer.value : '',
      items,
    };

    try {
      await api.put(`/invoices/${invoiceId}`, payload);
      toast.success('Invoice updated successfully');
      setTimeout(() => navigate('/invoices'), 1000);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update invoice');
    }
  };

  // Modern glassmorphism style (like your create page)
  const cardStyle = {
    background: 'rgba(255,255,255,0.95)',
    borderRadius: '2rem',
    boxShadow: '0 8px 48px 0 rgba(40, 110, 250, 0.10)',
    border: '1.5px solid rgba(180,210,255,0.18)',
    margin: '0 auto',
    maxWidth: 1100
  };

  const stickyBarStyle = {
    background: 'rgba(255,255,255,0.98)',
    borderRadius: '2rem',
    boxShadow: '0 2px 16px 0 rgba(40, 110, 250, 0.09)',
    border: '1.5px solid #e1e9f7',
    position: 'sticky',
    bottom: 10,
    zIndex: 2,
    maxWidth: 980,
    margin: '0 auto',
  };

  // react-select options
  const customerOptions = customers.map(c => ({ value: c.CustomerID, label: c.Name }));

  return (
    <div
      className="min-vh-100 d-flex flex-column"
      style={{
        background: 'linear-gradient(120deg, #b6d6fa 0%, #f0f6ff 100%)',
        minHeight: '100vh',
        paddingBottom: 60,
      }}
    >
       <BackButton></BackButton>
      <div className="container py-4 flex-grow-1 d-flex flex-column align-items-center" style={{ minHeight: '100vh' }}>
        <h2 className="mb-4 fw-bold text-primary text-center" style={{ letterSpacing: 1 }}>
          <span role="img" aria-label="edit">✏️</span> Edit Invoice
        </h2>
       
        <div className="card p-4 shadow-lg border-0 rounded-4 w-100 mb-4" style={cardStyle}>
          <form autoComplete="off" onSubmit={handleSubmit}>
            {/* Customer & Invoice Info */}
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Customer</label>
                <Select
                  classNamePrefix="react-select"
                  isSearchable
                  options={customerOptions}
                  value={selectedCustomer}
                  onChange={opt => {
                    setSelectedCustomer(opt);
                    setForm(f => ({ ...f, customerId: opt ? opt.value : '' }));
                  }}
                  placeholder="Type or select customer..."
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderRadius: 40,
                      paddingLeft: 3,
                      fontSize: 15,
                      minHeight: 39,
                    })
                  }}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Invoice Number</label>
                <input
                  type="text"
                  className="form-control rounded-pill px-3"
                  value={form.invoiceNumber}
                  readOnly
                  disabled
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Invoice Date</label>
                <input
                  type="date"
                  className="form-control rounded-pill px-3"
                  value={form.invoiceDate}
                  onChange={(e) => setForm({ ...form, invoiceDate: e.target.value })}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Due Date</label>
                <input
                  type="date"
                  className="form-control rounded-pill px-3"
                  value={form.dueDate}
                  onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Discount (₹)</label>
                <input
                  type="number"
                  className="form-control rounded-pill px-3"
                  value={form.discount}
                  onChange={(e) => setForm({ ...form, discount: e.target.value })}
                />
              </div>
              <div className="col-12">
                <label className="form-label">Notes</label>
                <textarea
                  className="form-control rounded-4"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Notes"
                ></textarea>
              </div>
            </div>

            {/* Items */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-semibold mb-0">Invoice Items</h5>
                <button type="button" className="btn btn-outline-primary btn-sm px-3 rounded-pill" onClick={addItem}>+ Add Item</button>
              </div>
              <div className="table-responsive">
                <table className="table table-bordered table-sm align-middle" style={{ fontSize: 15 }}>
                  <thead className="table-light">
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>Size</th>
                      <th>Qty</th>
                      <th>Rate</th>
                      <th>GST %</th>
                      <th>CGST %</th>
                      <th>SGST %</th>
                      <th>Amount</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <input className="form-control" placeholder="Description" value={item.Description} onChange={e => handleItemChange(idx, 'Description', e.target.value)} required />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              title="Search Product"
                              onClick={() => handleOpenProductModal(idx)}
                              tabIndex={-1}
                            >
                              <span role="img" aria-label="search">🔍</span>
                            </button>
                          </div>
                        </td>
                        <td><input className="form-control" placeholder="HSN" value={item.HSN} onChange={e => handleItemChange(idx, 'HSN', e.target.value)} /></td>
                        <td><input className="form-control" placeholder="Size" value={item.Size} onChange={e => handleItemChange(idx, 'Size', e.target.value)} /></td>
                        <td><input className="form-control" type="number" placeholder="Qty" value={item.Quantity} onChange={e => handleItemChange(idx, 'Quantity', e.target.value)} /></td>
                        <td><input className="form-control" type="number" placeholder="Rate" value={item.Rate} onChange={e => handleItemChange(idx, 'Rate', e.target.value)} /></td>
                        <td><input className="form-control" type="number" placeholder="GST %" value={item.GSTPercent} onChange={e => handleItemChange(idx, 'GSTPercent', e.target.value)} /></td>
                        <td><input className="form-control" placeholder="CGST %" value={item.CGST} readOnly tabIndex={-1} /></td>
                        <td><input className="form-control" placeholder="SGST %" value={item.SGST} readOnly tabIndex={-1} /></td>
                        <td><input className="form-control" placeholder="Amount" value={item.Amount} readOnly tabIndex={-1} /></td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm rounded-pill"
                            onClick={() => removeItem(idx)}
                            disabled={items.length === 1}
                            title="Remove item"
                          >✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sticky Summary Bar */}
            <div
              className="shadow rounded-4 p-3 mt-4 bg-white d-flex flex-wrap justify-content-between align-items-center"
              style={stickyBarStyle}
            >
              <span className="fw-bold" style={{ fontSize: 18 }}>Subtotal: <span className="text-primary">₹ {subTotal.toFixed(2)}</span></span>
              <span className="fw-bold" style={{ fontSize: 18 }}>Discount: <span className="text-primary">₹ {discount.toFixed(2)}</span></span>
              <span className="fw-bold" style={{ fontSize: 20 }}>Grand Total: <span className="text-success">₹ {grandTotal}</span></span>
            </div>
            <button
              type="submit"
              className="btn btn-primary w-100 py-3 rounded-pill fw-bold fs-5 mt-3"
            >Update Invoice</button>
          </form>
        </div>
      </div>

      {/* -------- Product Search Modal -------- */}
      {showProductModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            background: 'rgba(0,0,0,0.18)',
            zIndex: 2000,
          }}
          tabIndex={-1}
          onClick={() => setShowProductModal(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{ maxWidth: 480 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-content shadow-lg rounded-3">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">Search Product</h5>
                <button type="button" className="btn-close" onClick={() => setShowProductModal(false)} />
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control mb-3"
                  placeholder="Type product name or HSN"
                  value={productSearch}
                  onChange={e => {
                    setProductSearch(e.target.value);
                    fetchProductSearch(e.target.value);
                  }}
                  autoFocus
                />
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {productResults.length === 0 && productSearch && (
                    <div className="text-muted text-center py-2">No products found.</div>
                  )}
                  {productResults.map(prod => (
                    <button
                      key={prod.ProductID}
                      type="button"
                      className="list-group-item list-group-item-action mb-1"
                      style={{
                        borderRadius: 8,
                        border: '1px solid #eef1f8',
                        background: '#f8fbff',
                        display: 'block',
                        width: '100%',
                        textAlign: 'left'
                      }}
                      onClick={() => {
                        // Update the item row
                        const updated = [...items];
                        updated[productRowIdx] = {
                          ...updated[productRowIdx],
                          Description: prod.Name,
                          HSN: prod.HSN,
                          Rate: prod.Rate,
                          GSTPercent: prod.GSTPercent,
                        };
                        // recalc
                        const qty = parseFloat(updated[productRowIdx].Quantity) || 0;
                        const rate = parseFloat(prod.Rate) || 0;
                        const gstPercent = parseFloat(prod.GSTPercent) || 0;
                        const baseAmount = qty * rate;
                        const gstAmount = baseAmount * gstPercent / 100;
                        updated[productRowIdx].CGST = (gstPercent / 2).toFixed(2);
                        updated[productRowIdx].SGST = (gstPercent / 2).toFixed(2);
                        updated[productRowIdx].Amount = (baseAmount + gstAmount).toFixed(2);

                        setItems(updated);
                        setShowProductModal(false);
                      }}
                    >
                      <div className="fw-bold">{prod.Name}</div>
                      <div style={{ fontSize: 13 }}>
                        <span>HSN: {prod.HSN} &nbsp;&nbsp; Rate: ₹{prod.Rate} &nbsp;&nbsp; GST: {prod.GSTPercent}%</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
